// The Wodi price list CSV format, `wodi-price-list-csv-v1`
// (docs/plans/phase1-importers.md §4; columns from finance decision 17).
//
// One import schedules prices on one price list. The row validator is pure. The
// importer kind then checks each row against the catalogue and plans it with
// `planPeriodChange`, the same rule "Change price" uses, so an import never backdates a
// price, never stacks under a scheduled change and never edits an existing price row.
//
// A missing price is never zero (finance plan §4 rule 1). A blank price cell is an
// error; an explicit 0 is allowed and flagged, because a free item is a decision someone
// has to have made (finance decision 10).

import { oneOf, optional, text } from '../csv/cells.ts';
import type { ColumnSpec, RowCells, RowCheck, RowIssue } from '../csv/rows.ts';
import { readCells } from '../csv/rows.ts';
import type { ServiceItemCategory, ServiceItemKind } from '../catalogue.ts';
import { categoryMatchesKind, SERVICE_ITEM_CATEGORIES, SERVICE_ITEM_KINDS } from '../catalogue.ts';
import type { Money } from '../money.ts';
import { isNegativeMoney, isZeroMoney, parseMoney } from '../money.ts';
import type { CalendarDay } from '../time.ts';
import { compareDays, parseIsoDay } from '../time.ts';
import { blankToNull, hasErrors, refuse, take, warn } from './common.ts';

export const WODI_PRICE_LIST_FORMAT = 'wodi-price-list-csv-v1';

export type PriceListColumn =
  'code' | 'price' | 'starts_on' | 'name' | 'category' | 'kind' | 'billing_unit';

export const PRICE_LIST_COLUMNS: readonly (ColumnSpec<PriceListColumn> & {
  readonly description: string;
})[] = [
  {
    key: 'code',
    required: true,
    description: 'The item’s code in this organisation’s catalogue. Each code once per file.',
  },
  {
    key: 'price',
    required: true,
    description:
      'Price in KES per billing unit, such as 1500 or 1,500.50. Write 0 for a free item; a blank price is not allowed.',
  },
  {
    key: 'starts_on',
    required: false,
    description: 'The day the price starts, YYYY-MM-DD, after today. Blank: when the import says.',
  },
  {
    key: 'name',
    required: false,
    description: 'The item’s name. Needed only to create a service that isn’t in the catalogue.',
  },
  {
    key: 'category',
    required: false,
    description: `Needed only to create a service: ${SERVICE_ITEM_CATEGORIES.join(', ')}.`,
  },
  { key: 'kind', required: false, description: 'Needed only to create a service: service.' },
  {
    key: 'billing_unit',
    required: false,
    description: 'Needed only to create a service: what one unit is, such as each or test.',
  },
];

/** As the catalogue form (packages/shared catalogue.ts). */
export const MAX_ITEM_CODE_LENGTH = 40;
const ITEM_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]*$/;
export const MIN_ITEM_NAME_LENGTH = 2;
export const MAX_ITEM_NAME_LENGTH = 200;
export const MAX_BILLING_UNIT_LENGTH = 40;

export interface PriceListImportRow {
  readonly code: string;
  readonly price: Money;
  /** Null: the start the import chose for the whole file. */
  readonly startsOn: CalendarDay | null;
  /** What to create if the code isn't in the catalogue; null when the row doesn't say. */
  readonly newItem: {
    readonly name: string | null;
    readonly category: ServiceItemCategory | null;
    readonly kind: ServiceItemKind | null;
    readonly billingUnit: string | null;
  };
}

export type PriceRowIssueCode =
  | 'price.invalid_code'
  | 'price.missing'
  | 'price.negative'
  | 'price.free'
  | 'price.name_too_short'
  | 'price.category_kind_mismatch'
  | 'price.starts_in_past'
  | 'price.item_not_found'
  | 'price.item_inactive'
  | 'price.new_item_incomplete'
  | 'price.cannot_create_stock_item'
  | 'price.name_differs'
  | 'price.future_change_exists'
  | 'price.not_after_current_start'
  | 'price.changed_since_preview';

export const PRICE_ROW_MESSAGES: Record<PriceRowIssueCode, string> = {
  'price.invalid_code':
    'An item code is letters, digits, dots, dashes and slashes, starting with a letter or digit',
  'price.missing': 'The price is blank. Write the price, or 0 if the item is free.',
  'price.negative': 'A price cannot be negative',
  'price.free': 'This item will be free on this price list',
  'price.name_too_short': 'A name has at least two characters',
  'price.category_kind_mismatch':
    'Medicines and consumables are stock items; every other category is a service',
  'price.starts_in_past':
    'A price change must start after today, so charges already made keep their price',
  'price.item_not_found': 'There is no item with this code in the catalogue',
  'price.item_inactive': 'This item is no longer in use. Turn it back on before pricing it.',
  'price.new_item_incomplete':
    'To create this service, give its name, category, kind and billing unit',
  'price.cannot_create_stock_item':
    'Stock items can’t be created by a price import. Set the item up in stock first.',
  'price.name_differs':
    'The name differs from the catalogue. The price is imported; the name is not changed.',
  'price.future_change_exists':
    'A later price change is already scheduled for this item. Correct or remove that one first.',
  'price.not_after_current_start':
    'The current price starts on or after this day. To fix it, mark it entered in error.',
  'price.changed_since_preview':
    'This item or its price changed after the import was checked, so nothing was imported. Start the import again to check the new prices.',
};

const readers = {
  name: ['name', optional(text({ maxLength: MAX_ITEM_NAME_LENGTH }))],
  category: ['category', optional(oneOf(SERVICE_ITEM_CATEGORIES))],
  kind: ['kind', optional(oneOf(SERVICE_ITEM_KINDS))],
  billingUnit: ['billing_unit', optional(text({ maxLength: MAX_BILLING_UNIT_LENGTH }))],
} as const;

/** Validates one row of the format: everything one row can show on its own. */
export function validatePriceListRow(
  cells: RowCells<PriceListColumn>,
): RowCheck<PriceListImportRow> {
  const read = readCells(cells, readers);
  const issues: RowIssue[] = [...read.issues];

  const code = blankToNull(cells.code);
  if (code === null) refuse(issues, 'code', 'cell.required');
  else if (Array.from(code).length > MAX_ITEM_CODE_LENGTH) refuse(issues, 'code', 'cell.too_long');
  else if (!ITEM_CODE_PATTERN.test(code)) refuse(issues, 'code', 'price.invalid_code');

  let price: Money | null = null;
  if (blankToNull(cells.price) === null) refuse(issues, 'price', 'price.missing');
  else {
    price = take(issues, 'price', parseMoney(cells.price));
    if (price !== null && isNegativeMoney(price)) {
      refuse(issues, 'price', 'price.negative');
      price = null;
    } else if (price !== null && isZeroMoney(price)) warn(issues, 'price', 'price.free');
  }

  let startsOn: CalendarDay | null = null;
  const startsText = blankToNull(cells.starts_on);
  if (startsText !== null) {
    const parsed = parseIsoDay(startsText);
    if (parsed.ok) startsOn = parsed.value;
    else refuse(issues, 'starts_on', 'cell.invalid_date');
  }

  if (read.value !== null) {
    const { name, category, kind } = read.value;
    if (name !== null && Array.from(name).length < MIN_ITEM_NAME_LENGTH)
      refuse(issues, 'name', 'price.name_too_short');
    if (category !== null && kind !== null && !categoryMatchesKind(category, kind))
      refuse(issues, 'category', 'price.category_kind_mismatch');
  }

  if (read.value === null || code === null || price === null || hasErrors(issues)) {
    return { value: null, issues };
  }
  return { value: { code, price, startsOn, newItem: read.value }, issues };
}

/** The key two rows must not share: the code, as the catalogue stores it. */
export function priceListDedupeKey(row: PriceListImportRow): string {
  return row.code;
}

/**
 * The day a row's price starts must be after `today` (EAT). "Today" has already begun
 * at 00:00, and a price that started in the past would re-price charges already made.
 * The whole-file start ("now" or a future day) is checked by the import's parameters.
 */
export function priceStartIssues(startsOn: CalendarDay | null, today: CalendarDay): RowIssue[] {
  if (startsOn === null || compareDays(startsOn, today) > 0) return [];
  return [{ column: 'starts_on', code: 'price.starts_in_past', severity: 'error' }];
}

/**
 * For a code that isn't in the catalogue, when the import may create services: the row
 * must describe a complete service. Stock items are never created here, because they
 * need units and packs from the stock screens.
 */
export function newServiceIssues(row: PriceListImportRow): RowIssue[] {
  const { name, category, kind, billingUnit } = row.newItem;
  if (kind === 'stock_item' || (category !== null && !categoryMatchesKind(category, 'service'))) {
    return [{ column: 'kind', code: 'price.cannot_create_stock_item', severity: 'error' }];
  }
  const missing: RowIssue[] = [];
  if (name === null)
    missing.push({ column: 'name', code: 'price.new_item_incomplete', severity: 'error' });
  if (category === null)
    missing.push({ column: 'category', code: 'price.new_item_incomplete', severity: 'error' });
  if (kind === null)
    missing.push({ column: 'kind', code: 'price.new_item_incomplete', severity: 'error' });
  if (billingUnit === null)
    missing.push({ column: 'billing_unit', code: 'price.new_item_incomplete', severity: 'error' });
  return missing;
}
